import { UploadArtifactFileParamsArtifactType, api } from "@/api";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { action, computed, makeAutoObservable, observable } from "mobx";

interface SystemVoice {
  type: "system";
  voice_id: string;
  character_id: string;
  description: string;
  sample_url: string;
}

interface CharacterVoice {
  type: "user";
  character_id: string;
  voice_path: string;
}

type VoiceInfo = SystemVoice | CharacterVoice;

/**
 * 音色选项信息
 */
export interface VoiceOption {
  type: "system" | "user";
  /** 音色 ID */
  value: string;
  /** 音色名称 */
  label: string;
  /** 示例音频 URL */
  sampleUrl: string;
}

type SetKeys = keyof Pick<VoiceStore, "voices" | "loading" | "uploading">;

/**
 * 音色配置 Store
 * 管理音色选项信息和上传状态
 */
export class VoiceStore {
  @observable voices: VoiceOption[] = [];
  @observable loading: boolean = false;
  @observable uploading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  @computed get voiceList(): VoiceOption[] {
    return this.voices;
  }

  @action set<K extends SetKeys>(k: K, v: VoiceStore[K]) {
    this[k] = v as never;
  }

  /**
   * 获取音色选项列表
   * @param threadId 会话 ID
   */
  @action async fetchVoiceOptions(threadId: string) {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      const res = await api.threads.getThreadVoices(threadId);

      const voiceOptions: VoiceOption[] = (res.data as VoiceInfo[]).map(
        (voice) => ({
          type: voice.type,
          value: voice.type === "system" ? voice.voice_id : voice.voice_path,
          label:
            voice.type === "system"
              ? (voice.character_id || "")
                  .replaceAll("_", " ")
                  .split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
              : voice.character_id,
          sampleUrl:
            voice.type === "system"
              ? voice.sample_url
              : convertFilePathToUrl(voice.voice_path),
        })
      );

      const displayVoices = [
        ...voiceOptions.filter((item) => item.type === "user"),
        ...voiceOptions.filter((item) => item.type === "system"),
      ];

      this.set("voices", displayVoices);
    } finally {
      this.loading = false;
    }
  }

  /**
   * 上传自定义音色文件
   * @param threadId 当前会话 ID
   * @param file 音频文件
   * @returns 上传后的音色 ID
   */
  @action async uploadVoiceFile(threadId: string, file: File): Promise<string> {
    if (this.uploading) {
      return "";
    }

    this.uploading = true;

    try {
      const {
        data: { file_path },
      } = await api.threads.uploadArtifactFile(
        threadId,
        {
          artifact_type:
            UploadArtifactFileParamsArtifactType.ValueCharacterVoiceArtifact,
          data: "",
        },
        {
          file,
        }
      );

      // 上传成功后重新获取音色列表
      await this.fetchVoiceOptions(threadId);

      const newVoiceId = this.voices.find((item) => item.value === file_path);

      if (newVoiceId) {
        return newVoiceId.value;
      }

      return "";
    } finally {
      this.uploading = false;
    }
  }
}

export const voiceStore = new VoiceStore();
